/* import __COLOCATED_TEMPLATE__ from './view-mode-options.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency-decorators';
import ajax from 'embercom/lib/ajax';
import type ReportingStandalone from 'embercom/services/reporting-standalone';
import { type TaskGenerator } from 'ember-concurrency';
import type Report from 'embercom/models/reporting/custom/report';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import { hasFeature } from 'embercom/helpers/has-feature';
import type IntlService from 'ember-intl/services/intl';

interface Args {
  report: Report;
  duplicateReport: () => TaskGenerator<void>;
  deleteReport: () => TaskGenerator<void>;
  onClickEditReport: () => void;
  editReportDisabled: boolean;
  isStandalone: boolean;
  openShareSideDrawer: () => void;
  openExternalSharingSideDrawer?: () => void;
  openScheduleDeliveryDrawer?: () => void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Args;
}

const SNAPSHOT_URL = '/ember/reporting/custom_reports/snapshot';

export default class ReportingCustomReportViewModeOptions extends Component<Signature> {
  @service declare reportingStandalone: ReportingStandalone;
  @service declare reportingChartService: ReportingChartService;
  @service declare appService: AppService;
  @service notificationsService: any;
  @service declare intl: IntlService;

  get app() {
    return this.appService.app;
  }

  get shouldShowReportAction() {
    if (this.args.isStandalone) {
      return this.reportingStandalone.supportsFeature('showReportAction');
    }
    return true;
  }

  get shouldShowShareOption() {
    if (this.args.isStandalone) {
      return this.reportingStandalone.supportsFeature('shareReport');
    }
    return true;
  }

  get isExternalSharingEnabled() {
    return hasFeature('reporting-external-sharing', this.appService);
  }

  @task({ drop: true })
  *snapshotReport(): TaskGenerator<void> {
    yield this.postSnapshot();
  }

  private async postSnapshot() {
    let { id, updatedAt } = this.args.report;

    let updatedAtInSeconds = Math.floor(updatedAt.getTime() / 1000);

    try {
      return await ajax({
        url: SNAPSHOT_URL,
        type: 'POST',
        data: JSON.stringify({
          app_id: this.app.id,
          admin_id: this.app.currentAdmin.id,
          report_id: id,
          report_version: updatedAtInSeconds,
          synthetic_data_use_case: this.reportingChartService.syntheticDataUseCase,
        }),
      });
    } catch (error) {
      let errorMessage =
        error.jqXHR?.status === 409
          ? this.intl.t('reporting.custom-reports.report.stale-report-error')
          : this.intl.t('reporting.custom-reports.report.save-error');
      this.notificationsService.notifyError(errorMessage);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Custom::Report::ViewModeOptions': typeof ReportingCustomReportViewModeOptions;
    'reporting/custom/report/view-mode-options': typeof ReportingCustomReportViewModeOptions;
  }
}
